/**
 * FASE 10 — Admin Feature Flags page.
 *
 * Toggle on/off dei moduli feature-flagged (Noleggio, futuri).
 * Backend: GET/POST /api/admin/feature-flags[/{name}]
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, RefreshCw, ToggleLeft, ToggleRight, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/feature-flags`;

export default function AdminFeatureFlagsPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(API, { withCredentials: true });
      setCfg(data);
    } catch (e) {
      toast.error("Errore caricamento flags");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (name, currentEnabled) => {
    setSaving((s) => ({ ...s, [name]: true }));
    try {
      const { data } = await axios.post(
        `${API}/${name}`,
        { enabled: !currentEnabled },
        { withCredentials: true },
      );
      setCfg((prev) => ({
        ...prev,
        flags: { ...prev.flags, [name]: data.state },
      }));
      toast.success(`${name}: ${data.state.enabled ? "attivato" : "disattivato"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle fallito");
    } finally {
      setSaving((s) => ({ ...s, [name]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFCFA] pt-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#163321]" />
      </div>
    );
  }

  const flags = cfg?.flags || {};

  return (
    <div className="min-h-screen bg-[#FCFCFA] pt-6 pb-16"
          data-testid="admin-feature-flags-page">
      <Toaster position="top-right" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl text-[#0F1F14] flex items-center gap-3">
              Feature Flags
            </h1>
            <p className="text-sm text-[#4A5E51]">
              Attiva/disattiva moduli senza toccare il codice. Il codice
              rimane integralmente in produzione — si riattiva flippando il
              toggle.
            </p>
          </div>
          <button onClick={load} data-testid="ff-reload"
                  className="p-2 rounded hover:bg-slate-100">
            <RefreshCw className="w-4 h-4 text-[#163321]" />
          </button>
        </div>

        {Object.keys(flags).length === 0 ? (
          <div className="bg-white border border-[#E5E2DB] rounded-2xl p-10 text-center text-[#4A5E51]"
                data-testid="ff-empty">
            Nessun flag configurato.
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(flags).map(([name, meta]) => {
              const on = Boolean(meta.enabled);
              return (
                <div key={name}
                      className={`bg-white border rounded-2xl p-5 transition-colors ${
                        on ? "border-emerald-300 shadow-sm" : "border-[#E5E2DB]"
                      }`}
                      data-testid={`ff-card-${name}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-heading text-lg text-[#0F1F14] flex items-center gap-2">
                        {meta.label || name}
                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono text-slate-600">
                          {name}
                        </code>
                      </h3>
                      {!on && meta.suspended_message && (
                        <p className="mt-2 text-sm text-[#7A1D1D] bg-[#FDF0F0] rounded px-3 py-2"
                            data-testid={`ff-suspended-msg-${name}`}>
                          <Info className="w-3.5 h-3.5 inline mr-1" />
                          {meta.suspended_message}
                        </p>
                      )}
                      {meta.reason && (
                        <p className="mt-2 text-xs text-[#4A5E51] italic"
                            data-testid={`ff-reason-${name}`}>
                          Note: {meta.reason}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => toggle(name, on)}
                      disabled={saving[name]}
                      data-testid={`ff-toggle-${name}`}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        on
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                          : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                      }`}>
                      {saving[name] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : on ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                      {on ? "Attivo" : "Sospeso"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-6 text-xs text-[#7A8E83]">
          Modifica persistita in <code>/app/backend/data/feature_flags.json</code>.
          La UI pubblica legge il flag entro 60 secondi (cache in-memory).
        </p>
      </div>
    </div>
  );
}
